import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import useHelpers from '../../hooks/context/useHelpers';
import { getTierThresholdBounds } from '../../services/helpers/PricingHelpers';
import { applyContractDiscount } from '../../services/helpers/ContractDiscountHelpers';

export default function PricingTiers({ data, discount = 0, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Subscription:PricingTiers' });

  const { formatCurrency } = useHelpers();

  const formatThreshold = (index) => {
    const { from, to } = getTierThresholdBounds(data, index);

    return to === null ? `≥ ${formatCurrency(from)}` : `${formatCurrency(from)} - ${formatCurrency(to)}`;
  };

  const tagClassName = (index) =>
    classnames('w-full text-sm p-xs', {
      'bg-orange-50 text-orange-600 border-orange-200': index === 0,
      'bg-amber-50 text-amber-700 border-amber-200': index === 1,
      'bg-yellow-50 text-yellow-700 border-yellow-200': index === 2,
      'bg-lime-50 text-lime-700 border-lime-200': index >= 3 && index !== data.length - 1,
      '!bg-green-50 !text-green-700 !border-green-200': index === data.length - 1,
    });

  // The base fee (first tier) is never discounted; every other tier shows the
  // struck original next to the discounted price.
  const renderDiscountedTier = (item, index) => {
    const isBaseFee = index === 0;
    const discounted = applyContractDiscount(item.amount, discount);

    return (
      <Tag key={index} className={tagClassName(index)}>
        <span className="flex w-full items-center justify-between gap-2">
          <span>{t('label', { threshold: formatThreshold(index) })}</span>
          <span className="flex items-center gap-2">
            {!isBaseFee && <span className="line-through opacity-60">{formatCurrency(item.amount)}</span>}
            <span className="font-semibold">{formatCurrency(isBaseFee ? item.amount : discounted)}</span>
            <span className="rounded border border-current px-[6px] py-[1px] text-xs font-semibold">
              {isBaseFee ? t('no_discount') : t('discount_badge', { percentage: discount })}
            </span>
          </span>
        </span>
      </Tag>
    );
  };

  return (
    data?.length > 0 && (
      <div className={className}>
        {data.map((item, index) =>
          discount ? (
            renderDiscountedTier(item, index)
          ) : (
            <Tag key={index} className={tagClassName(index)}>
              {t('text', {
                amount: formatCurrency(item.amount),
                threshold: formatThreshold(index),
              })}
            </Tag>
          ),
        )}
      </div>
    )
  );
}
